import React, { memo, useEffect, useState } from "react";
import {
  FunnelBarData,
  FunnelChartState,
  CommunityStatus,
} from "@/components/Funnel/types";
import { Badge } from "../Funnel/Badge";
import { cn } from "@/lib/utils";
import { HalfFunnel } from "../Funnel/HalfFunnel";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../ui";

const getLeftSideBadge = (
  percent: number | undefined,
  count: number,
  faded = false,
  percentColor: string,
  countColor: string,
  bgColor?: string,
) => {
  const className = faded ? "opacity-30" : "opacity-100";
  return (
    <Badge
      percent={percent}
      className={className}
      count={count}
      percentColor={percentColor}
      countColor={countColor}
      bgColor={bgColor}
      showPillBg={true}
    />
  );
};

const getRightSideBadge = (
  percent: number | undefined,
  count: number,
  faded = false,
  percentColor: string,
  countColor: string,
) => {
  const className = faded ? "opacity-30" : "opacity-100";
  return (
    <Badge
      percent={percent}
      className={className}
      count={count}
      percentColor={percentColor}
      countColor={countColor}
      showPillBg={false}
    />
  );
};

export interface FunnelColors {
  rightBarColor: string;
  leftBarColor: string;
  leftWedgeColor: string;
  rightWedgeColor: string;
  percentColor: string;
  pillBackgroundColor: string;
}

export const SingleFunnel = memo(function SingleFunnel({
  isLoading,
  inCommunity,
  notInCommunity,
  colors,
  onBarClick,
  selectedStage = null,
  selectedCommunityStatus = null,
  fade = false,
  testId,
  allowNotInCommunitySelection,
}: {
  isLoading: boolean;
  inCommunity: FunnelBarData[];
  notInCommunity: FunnelBarData[];
  colors: FunnelColors;
  selectedStage?: FunnelChartState | null;
  selectedCommunityStatus?: CommunityStatus | "both" | null;
  onBarClick?: (
    stage: FunnelChartState,
    communityStatus: CommunityStatus,
  ) => void;
  fade: boolean;
  testId?: string;
  allowNotInCommunitySelection?: boolean;
}) {
  const { t } = useTranslation();

  const [inCommunityData, setInCommunityData] = useState<FunnelBarData[]>(
    inCommunity.map((row) => ({ ...row, percent: 0 })),
  );
  const [notInCommunityData, setNotInCommunityData] = useState<FunnelBarData[]>(
    notInCommunity.map((row) => ({ ...row, percent: 0 })),
  );

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => setInCommunityData(inCommunity), 0);
      setTimeout(() => setNotInCommunityData(notInCommunity), 0);
    }
  }, [inCommunity, notInCommunity, isLoading]);

  if (isLoading) {
    return <SkeletonChart rowCount={5} />;
  }

  if (inCommunity.length === 0 || notInCommunity.length === 0) return;

  const firstStageInCommunity = inCommunity[0];
  const firstStageNotInCommunity = notInCommunity[0];
  const leftBadgeFaded =
    selectedStage !== null &&
    (selectedStage !== firstStageInCommunity.stage ||
      selectedCommunityStatus !== "inCommunity");

  const rightBadgeFaded =
    selectedStage !== null &&
    (selectedStage !== firstStageNotInCommunity.stage ||
      selectedCommunityStatus !== "notInCommunity");

  return (
    <div className="max-w-md shrink grow" data-testid={testId}>
      <div className="z-1 flex">
        <div className="mr-2 pt-6 opacity-0">
          {getLeftSideBadge(
            firstStageInCommunity.percent || 100,
            firstStageInCommunity.count || 1,
            leftBadgeFaded,
            colors.leftBarColor,
            colors.leftBarColor,
            colors.pillBackgroundColor,
          )}
        </div>
        <div className="flex grow">
          <div className="animate-grow relative z-1 shrink grow basis-0">
            <div className="mb-2 h-5 text-xs leading-5 font-bold whitespace-nowrap">
              {t("zeemeeFunnel.chart.inCommunity")}
            </div>
            <HalfFunnel
              stages={inCommunityData}
              color={colors.leftBarColor}
              wedgeColor={colors.leftWedgeColor}
              percentColor={colors.leftBarColor}
              countColor={colors.leftBarColor}
              onClick={(stage) =>
                onBarClick && onBarClick(stage, "inCommunity")
              }
              fade={fade || !!selectedStage}
              side="left"
              getBadge={getLeftSideBadge}
              bgColor={colors.pillBackgroundColor}
              selectedState={
                selectedCommunityStatus !== "notInCommunity"
                  ? selectedStage
                  : null
              }
              wedgeClassName="bg-funnel-1-left-wedge"
            />
          </div>
          <div className="w-1"></div>
          <div className={"relative z-1 shrink grow basis-0"}>
            <div className="mb-2 h-5 text-xs leading-5 font-bold whitespace-nowrap">
              {t("zeemeeFunnel.chart.notInCommunity")}
            </div>
            <HalfFunnel
              side="right"
              stages={notInCommunityData}
              color={colors.rightBarColor}
              fade={fade || !!selectedStage}
              wedgeClassName="bg-funnel-1-right-wedge"
              onClick={
                allowNotInCommunitySelection && onBarClick
                  ? (stage) => onBarClick(stage, "notInCommunity")
                  : undefined
              }
              wedgeColor={colors.rightWedgeColor}
              percentColor={colors.percentColor}
              countColor={colors.rightBarColor}
              getBadge={getRightSideBadge}
              selectedState={
                selectedCommunityStatus !== "inCommunity" ? selectedStage : null
              }
            />
          </div>
        </div>
        <div className="ml-2 pt-6 opacity-0">
          {getRightSideBadge(
            firstStageNotInCommunity.percent,
            firstStageNotInCommunity.count,
            rightBadgeFaded,
            colors.percentColor,
            colors.leftBarColor,
          )}
        </div>
      </div>
    </div>
  );
});

const SkeletonChart = ({ rowCount }: { rowCount: number }) => {
  return (
    <div className="max-w-md shrink grow">
      <div className="z-1 flex">
        <div className="mr-2 pt-6 opacity-0"></div>
        <div className="flex grow">
          <div className="relative z-1 shrink grow basis-0">
            <div className="mb-2 h-5 text-xs leading-5 font-bold"></div>
            <SkeletonRows count={rowCount} side="left" />
          </div>
          <div className="w-1"></div>
          <div className={"relative z-1 shrink grow basis-0"}>
            <div className="mb-2 h-5 text-xs leading-5 font-bold"></div>
            <SkeletonRows count={rowCount} side="right" />
          </div>
        </div>
        <div className="ml-2 pt-6 opacity-0"></div>
      </div>
    </div>
  );
};

const SkeletonRows = ({
  count,
  side,
}: {
  count: number;
  side: "right" | "left";
}) => {
  let widthFraction = 2;
  return (
    <div className="w-full">
      {new Array(count).fill(null).map((_, idx) => {
        widthFraction *= 0.5;
        return (
          <div key={`${idx}-skeleton`} className="">
            <div
              className={cn("flex flex-grow", side === "left" && "justify-end")}
            >
              <Skeleton
                className="h-[40px]"
                style={{ width: `${widthFraction * 100}%` }}
              />
            </div>
            {idx !== count - 1 && <div className="h-4 w-full"></div>}
          </div>
        );
      })}
    </div>
  );
};
